// Generated by TaroBase SDK Generator: 2026-04-14T11:42:05.668Z
// Collection: chatMessages/$messageId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Anonymous community chat for the Artist Room. Anyone can read and post. Admin and founder can delete for moderation.
 */

export interface ChatMessagesRequest {
  displayName: string;
  message: string;
  timestamp: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface ChatMessagesResponse {
  displayName: string;
  message: string;
  timestamp: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a ChatMessages operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildChatMessages(messageId: string, data: ChatMessagesRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `chatMessages/${messageId}`, document: doc };
}

/**
 * Anyone can post a chat message with no authentication required. Display name and message are immutable after creation. Timestamp must be provided as Unix seconds. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setChatMessages(messageId: string, data: ChatMessagesRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`chatMessages/${messageId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting ChatMessages: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read chat messages. Displayed publicly in the Artist Room community chat.
   (Get Single Item)
 */
export async function getChatMessages(messageId: string): Promise<ChatMessagesResponse | null> {
  try {
    const result = await get(`chatMessages/${messageId}`);
    return result as ChatMessagesResponse | null;
  } catch (error) {
    console.error(`Error getting ChatMessages: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single ChatMessages document. (
  Read Operation Details: Anyone can read chat messages. Displayed publicly in the Artist Room community chat.
  )
 */
export function subscribeChatMessages(callback: (data: ChatMessagesResponse | null) => void, messageId: string): Promise<() => Promise<void>> {
  return subscribe(`chatMessages/${messageId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as ChatMessagesResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to ChatMessages at path '${`chatMessages/${messageId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many ChatMessages items from collection chatMessages
 
  Read Operation Details: Anyone can read chat messages. Displayed publicly in the Artist Room community chat.
  
 */
export async function getManyChatMessages(filter: string = ""): Promise<ChatMessagesResponse[]> {
  try {
    const results = await get("chatMessages", { prompt: filter });
    return Array.isArray(results) ? results as ChatMessagesResponse[] : [];
  } catch (error) {
    console.error(`Error getting ChatMessages collection at path 'chatMessages': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in ChatMessages collection at chatMessages
 
  Read Operation Details: Anyone can read chat messages. Displayed publicly in the Artist Room community chat.
  
 */
export function subscribeManyChatMessages(callback: (data: ChatMessagesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("chatMessages", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as ChatMessagesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to ChatMessages collection at path '${"chatMessages"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all ChatMessages items from collection chatMessages
 
  Read Operation Details: Anyone can read chat messages. Displayed publicly in the Artist Room community chat.
  
 */
export async function getAllChatMessages(filter: string = ""): Promise<ChatMessagesResponse[]> {
  try {
    const results = await get("chatMessages", { prompt: filter });
    return Array.isArray(results) ? results as ChatMessagesResponse[] : [];
  } catch (error) {
    console.error(`Error getting ChatMessages collection at path 'chatMessages': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in ChatMessages collection at chatMessages
 
  Read Operation Details: Anyone can read chat messages. Displayed publicly in the Artist Room community chat.
  
 */
export function subscribeAllChatMessages(callback: (data: ChatMessagesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("chatMessages", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as ChatMessagesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to ChatMessages collection at path '${"chatMessages"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count ChatMessages items in collection chatMessages.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countChatMessages(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("chatMessages", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting ChatMessages at path 'chatMessages': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on ChatMessages items in collection chatMessages.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateChatMessages(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("chatMessages", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating ChatMessages at path 'chatMessages': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Admin or founder only. Can remove messages for moderation purposes.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteChatMessages(messageId: string): Promise<boolean> {
  try {
    const succeeded = await set(`chatMessages/${messageId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting ChatMessages: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for ChatMessages for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteChatMessages(messageId: string): DocumentOperation {
  return { path: `chatMessages/${messageId}`, document: null };
}

